import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { arrayUnion, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import type { ActiveCall, MessageThread, User } from '../types';
import { freshCallParticipantIds, getActiveCall, primeRingtone, startRingtone } from '../utils/calls';
import PhoneIcon from './icons/PhoneIcon';
import VideoCameraIcon from './icons/VideoCameraIcon';

interface CallWatcherProps {
  threads: MessageThread[];
  currentUser: User | null;
  allUsers: User[];
  // True while this user is already on a call screen.
  busy: boolean;
  // True when the OS rings incoming calls natively (CallKit / full-screen notification), so
  // the in-app ring screen stays out of the way.
  disabled?: boolean;
  onAccept: (thread: MessageThread, call: ActiveCall) => void;
}

// A call only rings for this long after it starts - after that the chat's green
// "join" bubble is the way in.
const RING_WINDOW_MS = 40000;

// Rings the user for incoming 1:1 calls while the app is open. (Group/crew
// calls don't ring - they show the green join bubble in the chat.)
const CallWatcher: React.FC<CallWatcherProps> = ({ threads, currentUser, allUsers, busy, disabled, onAccept }) => {
  // Unlock sound playback on the first tap so the ringtone can play when a call arrives.
  useEffect(() => { primeRingtone(); }, []);
  const [now, setNow] = useState(Date.now());
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const hasAnyCall = threads.some((t) => t.activeCall);

  useEffect(() => {
    if (!hasAnyCall) return;
    const t = window.setInterval(() => setNow(Date.now()), 2000);
    return () => window.clearInterval(t);
  }, [hasAnyCall]);

  const incoming = useMemo(() => {
    if (!currentUser || busy || disabled) return null;
    // Always judge calls against the real clock. `now` is only refreshed every couple of seconds (and not at all
    // while no call exists), so after a long-open tab it was hours stale: a call written after that moment looked
    // "from the future" and rang even when it was old, which made the ringtone start at random.
    const clock = Date.now();
    for (const thread of threads) {
      if (thread.isGroup || thread.listingId) continue;
      const call = getActiveCall(thread, clock);
      if (!call || call.startedBy.id === currentUser.id || dismissed.has(call.id)) continue;
      if ((call.declinedBy || []).includes(currentUser.id)) continue;
      const fresh = freshCallParticipantIds(call, clock);
      if (fresh.includes(currentUser.id) || fresh.length !== 1 || fresh[0] !== call.startedBy.id) continue;
      if (clock - call.startedAt > RING_WINDOW_MS || call.startedAt > clock + 10000) continue;
      // The caller's app pings every 15s while the call is live; a stale ping means the call is dead
      // (caller crashed/left) and must never ring.
      const callerPing = Number(call.participants?.[call.startedBy.id] || 0);
      if (clock - callerPing > 30000) continue;
      return { thread, call };
    }
    return null;
  }, [threads, currentUser, busy, now, dismissed]);

  const ringingId = incoming?.call.id;
  const stopRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    if (!ringingId) return;
    stopRef.current = startRingtone();
    return () => { stopRef.current?.(); stopRef.current = null; };
  }, [ringingId]);

  if (!incoming || !currentUser) return null;
  const { thread, call } = incoming;
  const caller = allUsers.find((u) => u.id === call.startedBy.id) || thread.participants?.find((p) => p.id === call.startedBy.id);
  const avatar = (caller as any)?.avatarUrl || '/logo.png';
  const kind = call.type === 'video' ? 'video' : 'audio';

  const decline = () => {
    setDismissed((prev) => new Set(prev).add(call.id));
    updateDoc(doc(db, 'messageThreads', thread.id), { 'activeCall.declinedBy': arrayUnion(currentUser.id) }).catch(() => undefined);
  };
  const accept = () => {
    setDismissed((prev) => new Set(prev).add(call.id));
    onAccept(thread, call);
  };

  return createPortal(
    <div className="fixed inset-0 z-[1450] flex flex-col items-center justify-between bg-[radial-gradient(circle_at_top,#1f2937,#0b0d10_70%)] px-6 text-white" role="alertdialog" aria-label="Incoming call"
      style={{ paddingTop: 'calc(var(--sat-native, env(safe-area-inset-top)) + 4rem)', paddingBottom: 'calc(var(--sab-native, env(safe-area-inset-bottom)) + 3rem)' }}>
      <div className="flex flex-col items-center gap-4">
        <div className="relative h-36 w-36">
          <span className="absolute inset-0 animate-ping rounded-[28%] bg-lime-400/30" />
          <img src={avatar} alt="" className="relative h-full w-full rounded-[28%] border-4 border-white/10 object-cover" />
        </div>
        <p className="text-3xl font-black">{'@' + String(call.startedBy.username).toUpperCase()}</p>
        <p className="text-base font-semibold text-gray-300">Incoming {kind} call…</p>
      </div>
      <div className="flex items-center gap-16">
        <div className="flex flex-col items-center gap-2">
          <button type="button" onClick={decline} aria-label="Decline call" className="flex h-16 w-16 items-center justify-center rounded-full bg-red-500 shadow-lg transition active:scale-90 hover:bg-red-400">
            <PhoneIcon className="h-8 w-8 rotate-[135deg]" />
          </button>
          <span className="text-xs font-semibold text-gray-300">Decline</span>
        </div>
        <div className="flex flex-col items-center gap-2">
          <button type="button" onClick={accept} aria-label="Accept call" className="flex h-16 w-16 items-center justify-center rounded-full bg-lime-500 text-black shadow-lg transition active:scale-90 hover:bg-lime-400">
            {call.type === 'video' ? <VideoCameraIcon className="h-8 w-8" /> : <PhoneIcon className="h-8 w-8" />}
          </button>
          <span className="text-xs font-semibold text-gray-300">Accept</span>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CallWatcher;
